import { complianceDocumentDefinitionEvents } from '@texas/components/configuration/complianceDocumentDefinitions/events';
import { supplierComplianceDocumentEvents } from '@texas/components/suppliers/complianceDocuments/events';
import { reactEvents } from '@bridge/reactEvents';
import { variantsApi } from '@texas/api/endpoints/variantsApi';
import { useApiResource } from '@texas/api/hooks/useApiResource';
import { useArticleContext } from '@texas/components/article/articleContext/useArticleContext';
import { articleManageEvents } from '@texas/components/article/manage/events';
import { inquiryEvents } from '@texas/components/article/variant/inquiry/events';
import { offerEvents } from '@texas/components/article/variant/offers/events';
import { compositionVersionEvents } from '@texas/components/article/variant/versions/events';
import { compositionGroupEvents } from '@texas/components/shared/compositionGroup/events';
import { useSignalSubscriptionEvents } from '@texas/components/shared/hooks/useSignalSubscriptionEvents';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

export interface ArticleNavigationActivity {
  label: string;
  tone: 'attention' | 'muted' | 'success' | 'danger';
}

const EMPTY_BRANCH_IDS: number[] = [];
const SUMMARY_EVENTS = [
  reactEvents.newOptionAddedWithVariant,
  reactEvents.variantOptionLinked,
  reactEvents.variantArchived,
  reactEvents.variantRestored,
  reactEvents.supplierAdded,
  reactEvents.variantSupplierChanged,
  reactEvents.articleVersionUpdated,
  articleManageEvents.branchesModified,
  complianceDocumentDefinitionEvents.changed,
  compositionGroupEvents.onCreate,
  compositionGroupEvents.onCopy,
  compositionGroupEvents.onArchived,
  compositionGroupEvents.onRestored,
  compositionVersionEvents.onVersionsPatched,
  compositionVersionEvents.onControllerVersionCreated,
  compositionVersionEvents.onControllerVersionRestored,
  inquiryEvents.onUpdated,
  offerEvents.onUpdated,
];
const SYNC_EVENTS = [reactEvents.variantFinalized, reactEvents.garpDataUpdated];

interface Options {
  articleId: number;
  showGarp: boolean;
  syncedVariantId: number | null;
}

export function useArticleNavigationActivity(
  variantId: number | null,
  { articleId, showGarp, syncedVariantId }: Options,
) {
  const { t } = useTranslation();
  const context = useArticleContext();
  const [summaryVariantId, setSummaryVariantId] = useState<number | null>(null);
  const branchIds =
    context.articleId === articleId ? context.branchIds : EMPTY_BRANCH_IDS;
  const isSyncedVariant = variantId !== null && variantId === syncedVariantId;

  const {
    data: summary,
    loading,
    refetch: refetchSummary,
  } = useApiResource(variantsApi.getNavigationSummary);
  const { data: syncChanges, refetch: refetchSyncChanges } = useApiResource(
    variantsApi.getSyncChanges,
  );
  const currentSummary = summaryVariantId === variantId ? summary : null;
  const isBranchVisible = useCallback(
    (branchId: number) =>
      branchIds.length === 0 || branchIds.includes(branchId),
    [branchIds],
  );
  const isSelectedScopeSynced =
    isSyncedVariant &&
    currentSummary !== null &&
    currentSummary.branches.some((branch) =>
      isBranchVisible(branch.branchId),
    ) &&
    currentSummary.branches
      .filter((branch) => isBranchVisible(branch.branchId))
      .every((branch) => Boolean(branch.lastSync));

  const refreshSummary = useCallback(() => {
    if (variantId === null) return Promise.resolve();

    return refetchSummary(
      variantId,
      branchIds.length > 0 ? branchIds : undefined,
    );
  }, [branchIds, refetchSummary, variantId]);

  useEffect(() => {
    if (variantId === null) return;

    let active = true;
    setSummaryVariantId(null);
    refreshSummary().then(() => {
      if (active) setSummaryVariantId(variantId);
    });

    return () => {
      active = false;
    };
  }, [refreshSummary, variantId]);

  useEffect(() => {
    return inquiryEvents.onVariantUpdated.subscribe((updatedVariantId) => {
      if (updatedVariantId === variantId) void refreshSummary();
    });
  }, [refreshSummary, variantId]);

  const refreshFromEvent = useCallback(() => {
    if (variantId !== null) void refreshSummary();
  }, [refreshSummary, variantId]);
  useSignalSubscriptionEvents({
    triggerOnEvent: refreshFromEvent,
    signalEvents: SUMMARY_EVENTS,
  });
  useEffect(() => {
    const refreshSupplier = (supplierId: number) => {
      if (currentSummary?.supplierIds.includes(supplierId)) refreshFromEvent();
    };
    const unsubscribe = [
      supplierComplianceDocumentEvents.changed.subscribe(refreshSupplier),
      reactEvents.supplierArchived.subscribe(refreshSupplier),
      reactEvents.supplierRestored.subscribe(refreshSupplier),
    ];
    return () => unsubscribe.forEach((stop) => stop());
  }, [currentSummary, refreshFromEvent]);

  useEffect(
    () =>
      articleManageEvents.certificatesModified.subscribe((updatedArticleId) => {
        if (updatedArticleId === articleId) refreshFromEvent();
      }),
    [articleId, refreshFromEvent],
  );

  useEffect(() => {
    window.addEventListener('focus', refreshFromEvent);
    return () => window.removeEventListener('focus', refreshFromEvent);
  }, [refreshFromEvent]);

  useEffect(() => {
    return reactEvents.articleTasksChanged.subscribe((articleId) => {
      if (articleId === context.articleId) refreshFromEvent();
    });
  }, [context.articleId, refreshFromEvent]);

  useEffect(() => {
    if (variantId === null || !showGarp || !isSelectedScopeSynced) return;

    refetchSyncChanges(variantId);
  }, [isSelectedScopeSynced, refetchSyncChanges, showGarp, variantId]);

  const refreshAfterSync = useCallback(() => {
    if (variantId === null) return;
    void refreshSummary();
    if (showGarp && isSelectedScopeSynced) void refetchSyncChanges(variantId);
  }, [
    isSelectedScopeSynced,
    refetchSyncChanges,
    refreshSummary,
    showGarp,
    variantId,
  ]);
  useSignalSubscriptionEvents({
    triggerOnEvent: refreshAfterSync,
    signalEvents: SYNC_EVENTS,
  });

  const activity = useMemo<
    Record<string, ArticleNavigationActivity | undefined>
  >(() => {
    const changedFieldCount =
      syncChanges
        ?.filter((branch) => isBranchVisible(branch.branchId))
        .reduce(
          (total, branch) =>
            total +
            branch.modifications.reduce(
              (branchTotal, modification) =>
                branchTotal + modification.modifiedFields.length,
              0,
            ),
          0,
        ) ?? 0;

    const optionCount = currentSummary?.optionCount ?? 0;
    const versionCount = currentSummary?.versionCount ?? 0;
    const supplierCount = currentSummary?.supplierCount ?? 0;
    const invalidSupplierCount = currentSummary?.invalidSupplierCount ?? 0;
    const openInquiryCount = currentSummary?.openInquiryCount ?? 0;
    const sentInquiryCount = currentSummary?.sentInquiryCount ?? 0;
    const draftOfferCount = currentSummary?.draftOfferCount ?? 0;

    return {
      'variant.options':
        optionCount > 0
          ? {
              label: t('navigation.optionsCount', { count: optionCount }),
              tone: 'muted',
            }
          : undefined,
      'variant.versions':
        versionCount > 0
          ? {
              label: t('navigation.versionsCount', { count: versionCount }),
              tone: 'muted',
            }
          : undefined,
      'variant.suppliers':
        supplierCount > 0
          ? {
              label: t('navigation.suppliersCount', { count: supplierCount }),
              tone: 'muted',
            }
          : undefined,
      'variant.certificates':
        invalidSupplierCount > 0
          ? {
              label: t('supplierComplianceCoverage.invalidSuppliers', {
                count: invalidSupplierCount,
              }),
              tone: 'danger',
            }
          : undefined,
      'variant.inquiry':
        openInquiryCount > 0 || sentInquiryCount > 0
          ? {
              label: t('navigation.inquiryCounts', {
                draft: openInquiryCount,
                sent: sentInquiryCount,
              }),
              tone: 'attention',
            }
          : undefined,
      'variant.offers':
        draftOfferCount > 0
          ? {
              label: t('navigation.draftsCount', { count: draftOfferCount }),
              tone: 'muted',
            }
          : undefined,
      'variant.general.garp': isSelectedScopeSynced
        ? {
            label:
              changedFieldCount > 0
                ? t('navigation.changedCount', { count: changedFieldCount })
                : t('navigation.synced'),
            tone: changedFieldCount > 0 ? 'attention' : 'success',
          }
        : undefined,
    };
  }, [currentSummary, isBranchVisible, isSelectedScopeSynced, syncChanges, t]);

  return {
    activity,
    branches: currentSummary?.branches ?? [],
    loading,
    openTaskCount: currentSummary?.openTaskCount ?? 0,
    ready: currentSummary !== null,
  };
}
